import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSelectedRestaurant } from '../context/SelectedRestaurantContext';
import { useLogout } from '../hooks/useLogout';
import ErrorModal from '../components/ErrorModal';
import { ApiError } from '../services/http';
import { adminRestaurantsService } from '../services/adminRestaurants';
import { subscriptionService } from '../services/subscriptionService';
import RestaurantSelector from '../components/RestaurantSelector';
import ProtectedRoute from '../components/ProtectedRoute';
import { useTutorialRefs } from '../context/TutorialRefsContext';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { user, isOwner, isManagerOrKitchen, isKitchen, isManager, isWaiter } = useAuth();
  const { selectedRestaurantId, selectedRestaurantName, selectedRestaurantLogo, selectedRestaurantStatus, setSelectedRestaurant, isLoading: restaurantLoading } = useSelectedRestaurant();
  const { logout, isLoading: logoutLoading } = useLogout();
  // Mostrar menú por defecto en todas las rutas /admin/* excepto en /admin
  const [showQuickMenu, setShowQuickMenu] = useState(() => location.pathname !== '/admin');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });

  // Obtener refs del contexto de tutorial
  const { menuButtonRef, restaurantSelectorRef } = useTutorialRefs();

  // Escuchar evento global para abrir el modal de estado desde otras vistas (e.g., cards en Restaurants)
  useEffect(() => {
    const openStatusHandler = () => setShowStatusModal(true);
    window.addEventListener('ots:openStatusModal', openStatusHandler);
    return () => {
      window.removeEventListener('ots:openStatusModal', openStatusHandler);
    };
  }, []);

  // Mantener lógica: siempre visible fuera de /admin, oculto en /admin
  useEffect(() => {
    if (location.pathname === '/admin') {
      setShowQuickMenu(false);
    } else if (location.pathname.startsWith('/admin')) {
      setShowQuickMenu(true);
    }
  }, [location.pathname]);

  const getPageTitle = () => {
    switch (location.pathname) {
      case '/admin':
        return '';
      case '/admin/restaurants':
        return 'Restaurantes';
      case '/admin/menu':
        return 'Menú';
      case '/admin/responsibles':
        return 'Responsables';
      case '/admin/waiters':
        return 'Meseros';
      case '/admin/tables':
        return 'Mesas';
      case '/admin/kitchen':
        return 'Cocina';
      case '/admin/sales':
        return 'Ventas';
      case '/admin/analytics':
        return 'Analytics';
      case '/admin/promotions':
        return 'Promociones';
      default:
        return 'Admin';
    }
  };

  const getPageIcon = () => {
    switch (location.pathname) {
      case '/admin':
        return '';
      case '/admin/restaurants':
        return '🏪';
      case '/admin/menu':
        return '🍽️';
      case '/admin/responsibles':
        return '👥';
      case '/admin/waiters':
        return '/assets/images/mesero.png';
      case '/admin/tables':
        return '/assets/images/mesita.png';
      case '/admin/kitchen':
        return '👨‍🍳';
      case '/admin/sales':
        return '💰';
      case '/admin/analytics':
        return '📈';
      case '/admin/promotions':
        return '🎉';
      default:
        return '⚙️';
    }
  };

  const getUserRoleLabel = (): string => {
    try {
      if (isOwner()) return 'Propietario';
      if (isKitchen()) return 'Cocina';
      if (isManager()) return 'Manager';
      if (isWaiter()) return 'Mesero';
      return 'Cliente';
    } catch {
      return 'Cliente';
    }
  };

  // Imagen de perfil (Firebase) con fallback a providerData
  const profileImageUrl = (user as any)?.photoURL || (user as any)?.providerData?.[0]?.photoURL || '';
  // En /admin usar foto de usuario; en otras rutas usar logo del restaurante si existe
  const displayProfileImageUrl = profileImageUrl; //(location.pathname === '/admin')
   // ? profileImageUrl
   // : (selectedRestaurantLogo || profileImageUrl);
  const [imgError, setImgError] = useState(false);
  const showProfileImg = !!displayProfileImageUrl && !imgError;
  // Resetear error cuando cambia la imagen a mostrar
  useEffect(() => { setImgError(false); }, [displayProfileImageUrl]);

  const handleBack = () => {
    if (location.pathname === '/admin') {
              navigate('/profile');
    } else {
      navigate('/admin');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      // El hook useLogout ya maneja la navegación y limpieza completa
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // No mostrar modal aquí; el hook de logout navega y limpia sesión
    }
  };

  const handleBillingPortal = async () => {
    try {
      const { url } = await subscriptionService.createBillingPortalSession();
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al abrir portal de facturación:', error);
      setErrorModal({ 
        open: true, 
        title: 'Error', 
        message: 'No se pudo abrir el portal de facturación. Inténtalo de nuevo más tarde.' 
      });
    }
  };

  const handleToggleRestaurantStatus = async () => {
    if (!user?.uid || !selectedRestaurantId) {
      setErrorModal({ open: true, title: 'Datos faltantes', message: 'No hay restaurante seleccionado' });
      return;
    }

    setIsChangingStatus(true);
    try {
      const newStatus = selectedRestaurantStatus === 'open' ? false : true;
      
      // Llamar al servicio para cambiar el estado
      await adminRestaurantsService.toggleRestaurantStatus(
        user.uid, 
        selectedRestaurantId, 
        newStatus
      );
      
      // Actualizar el contexto local
      await setSelectedRestaurant(
        selectedRestaurantId, 
        selectedRestaurantName, 
        selectedRestaurantLogo || undefined,
        newStatus ? 'open' : 'closed'
      );
      // Notificar a otras vistas para actualización en caliente
      window.dispatchEvent(new CustomEvent('ots:restaurantStatusChanged', {
        detail: {
          restaurantId: selectedRestaurantId,
          isOpen: newStatus
        }
      }));
      
      setShowStatusModal(false);
      console.log('✅ Estado del restaurante cambiado exitosamente');
    } catch (error) {
      console.error('❌ Error cambiando estado del restaurante:', error);
      const message = error instanceof ApiError && error.message ? error.message : 'Error al cambiar el estado del restaurante';
      setErrorModal({ open: true, title: 'Error', message });
    } finally {
      setIsChangingStatus(false);
    }
  };

  const allAdminMenuItems = [
    { path: '/admin', label: '', icon: '🏠' },
    { path: '/admin/restaurants', label: 'Restaurantes', icon: '🏪' },
    { path: '/admin/menu', label: 'Menú', icon: '🍽️' },
    { path: '/admin/waiters', label: '', icon: '/assets/images/mesero.png' },
    { path: '/admin/reservations', label: 'Reservaciones', icon: '📅' },
    { path: '/admin/responsibles', label: 'Responsables', icon: '👥' },
    { path: '/admin/tables', label: '', icon: '/assets/images/mesita.png' },
    { path: '/admin/kitchen', label: 'Cocina', icon: '👨‍🍳' },
    { path: '/admin/sales', label: 'Reportes', icon: '📈' },
    { path: '/admin/analytics', label: 'IA', icon: '🤖' },
    { path: '/admin/promotions', label: 'Promos', icon: '🎉' },
  ];

  const adminMenuItems = isKitchen() 
    ? allAdminMenuItems.filter(item => item.path === '/admin/kitchen')
    : allAdminMenuItems;

  // Redirección automática de cocinero al módulo de cocina
  if (isKitchen() && location.pathname === '/admin') {
    // Evitar navegación durante render; usar efecto y replace
    setTimeout(() => navigate('/admin/kitchen', { replace: true }), 0);
  }

  return (
    <ProtectedRoute requireAuth={true} requireAdmin={true}>
      <div className="min-h-screen bg-[#e9ecef]">
        {/* Header principal - Completamente responsive */}
        <div className="bg-[#22B6B2] dark:bg-[#2C4358] shadow-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
            {/* Header para móviles */}
            <div className="block sm:hidden">
              <div className="flex items-center justify-between py-2">
                {/* Lado izquierdo móvil */}
                <div className="flex items-center space-x-1">
                  <button
                    onClick={handleBack}
                    className="p-2 text-white hover:text-white/80 hover:bg-white/10 rounded-lg transition-colors text-2xl"
                  >
                    ←
                  </button>
                  
                  <button
                    ref={menuButtonRef}
                    onClick={() => setShowQuickMenu(!showQuickMenu)}
                    className={`p-2 rounded-lg transition-colors text-2xl ${
                      showQuickMenu 
                        ? 'text-white bg-white/20 hover:bg-white/30' 
                        : 'text-white hover:text-white/80 hover:bg-white/10'
                    }`}
                    title={showQuickMenu ? 'Ocultar menú' : 'Mostrar menú'}
                  >
                    ☰
                  </button>
                </div>

                {/* Centro móvil - Selector de restaurante */}
                <div className="flex-1 mx-2">
                  {(isOwner() || isManagerOrKitchen()) ? (
                    <div ref={restaurantSelectorRef}>
                      <RestaurantSelector 
                        showLoading={restaurantLoading}
                        showInfo={false}
                        isHeader={true}
                      />
                    </div>
                  ) : (
                    selectedRestaurantName && (
                      <div className="flex items-center justify-center space-x-1 bg-blue-50 px-2 py-1 rounded text-xs">
                        <span className="text-blue-600">
                          <img src={selectedRestaurantLogo || ''} alt="Logo restaurante" className="w-6 h-6 object-contain" />
                        </span>
                        <span className="text-blue-600 font-medium truncate">
                          {selectedRestaurantName}
                        </span>
                      </div>
                    )
                  )}
                </div>

                {/* Lado derecho móvil */}
                <div className="flex items-center space-x-1">
                  <div className="relative">
                    <button
                      onClick={() => setShowProfileMenu(!showProfileMenu)}
                      className="p-1 rounded-full hover:bg-white/10 transition-colors"
                      title="Perfil"
                      aria-label="Abrir menú de perfil"
                    >
                      {showProfileImg ? (
                        <img src={displayProfileImageUrl} alt="Perfil" className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" onError={() => setImgError(true)} />
                      ) : (
                        <span className="p-2 text-white text-lg">👤</span>
                      )}
                    </button>
                    {showProfileMenu && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900">
                            {user?.displayName || user?.email}
                          </p>
                          <p className="text-xs text-gray-500">
                            {getUserRoleLabel()}
                          </p>
                        </div>
                        <div className="py-1">
                          <button
                            onClick={() => navigate('/pricing')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            💰 Planes y Precios
                          </button>
                          <button
                            onClick={handleBillingPortal}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            👤 Suscripción
                          </button>
                          <button
                            onClick={handleLogout}
                            disabled={logoutLoading}
                            className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                              logoutLoading 
                                ? 'text-gray-400 cursor-not-allowed' 
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                          >
                            {logoutLoading ? '🔄 Cerrando...' : '🚪 Cerrar Sesión'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Header para tablets y desktop */}
            <div className="hidden sm:block">
              <div className="flex items-center justify-between h-12">
                {/* Lado izquierdo */}
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleBack}
                    className="p-2 text-white hover:text-white/80 hover:bg-white/10 rounded-lg transition-colors text-2xl"
                  >
                    ←
                  </button>

                  {/* Botón de menú */}
                  <div className="relative">
                    <button
                      ref={menuButtonRef}
                      onClick={() => setShowQuickMenu(!showQuickMenu)}
                      className={`p-2 rounded-lg transition-colors text-2xl ${
                        showQuickMenu 
                          ? 'text-white bg-white/20 hover:bg-white/30' 
                          : 'text-white hover:text-white/80 hover:bg-white/10'
                      }`}
                      title={showQuickMenu ? 'Ocultar menú rápido' : 'Mostrar menú rápido'}
                    >
                      ☰
                    </button>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {getPageIcon().startsWith('/') ? (
                      <img 
                        src={getPageIcon()} 
                        alt={getPageTitle()}
                        className="w-6 h-6 object-contain"
                      />
                    ) : (
                      <span className="text-lg text-white">{getPageIcon()}</span>
                    )}
                    <h1 className="text-lg font-bold text-white">
                      {getPageTitle()}
                    </h1>
                  </div>
                </div>

                {/* Centro - Selector de restaurante */}
                <div className="flex-1 flex justify-center max-w-md">
                  {(isOwner() || isManagerOrKitchen()) ? (
                    <div ref={restaurantSelectorRef} style={{margin: 0}}>
                      <RestaurantSelector 
                        showLoading={restaurantLoading}
                        showInfo={false}
                        isHeader={true}
                      />
                    </div>
                  ) : (
                    selectedRestaurantName && (
                      <div className="flex items-center space-x-2 bg-blue-50 px-3 py-2 rounded-lg">
                        <span className="text-blue-600">
                          <img src={selectedRestaurantLogo || ''} alt="Logo restaurante" className="w-6 h-6 object-contain" />
                        </span>
                        <span className="text-blue-600 font-medium text-sm">
                          {selectedRestaurantName}
                        </span>
                      </div>
                    )
                  )}
                </div>

                {/* Lado derecho */}
                <div className="flex items-center space-x-2">
                  {/* Botón de perfil */}
                  <div className="relative">
                    <button
                      onClick={() => setShowProfileMenu(!showProfileMenu)}
                      className="p-1 rounded-full hover:bg-white/10 transition-colors"
                      title="Perfil"
                      aria-label="Abrir menú de perfil"
                    >
                      {showProfileImg ? (
                        <img src={displayProfileImageUrl} alt="Perfil" className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" onError={() => setImgError(true)} />
                      ) : (
                        <span className="p-2 text-white text-lg">👤</span>
                      )}
                    </button>
                    
                    {/* Menú de perfil desplegable */}
                    {showProfileMenu && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900">
                            {user?.displayName || user?.email}
                          </p>
                          <p className="text-xs text-gray-500">
                            {getUserRoleLabel()}
                          </p>
                        </div>
                        
                        <div className="py-1">
                          <button
                            onClick={() => navigate('/pricing')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            💰 Planes y Precios
                          </button>
                          <button
                            onClick={handleBillingPortal}
                            className="w-full text-left px-4 py-4 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            👤 Suscripción
                          </button>
                          <button
                            onClick={handleLogout}
                            disabled={logoutLoading}
                            className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                              logoutLoading 
                                ? 'text-gray-400 cursor-not-allowed' 
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                          >
                            {logoutLoading ? '🔄 Cerrando...' : '🚪 Cerrar Sesión'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navegación secundaria - Responsive */}
        {showQuickMenu && (
          <div className="bg-[#e9ecef] border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
              {/* Navegación móvil */}
              <div className="block sm:hidden">
                <nav className="grid grid-cols-2 gap-2 py-3">
                  {adminMenuItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`flex items-center justify-center space-x-2 py-3 px-2 rounded-lg font-medium text-sm transition-colors ${
                        location.pathname === item.path
                          ? 'bg-blue-50 text-blue-600 border border-blue-200'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                      }`}
                    >
                      {item.icon.startsWith('/') ? (
                        <img 
                          src={item.icon} 
                          alt={item.label}
                          className="w-5 h-5 object-contain"
                        />
                      ) : (
                        <span className="text-lg">{item.icon}</span>
                      )}
                      <span className="truncate">{item.label}</span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Navegación desktop */}
              <div className="hidden sm:block">
                <nav className="flex space-x-8 overflow-x-auto py-3">
                  {adminMenuItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                        location.pathname === item.path
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {item.icon.startsWith('/') ? (
                        <img 
                          src={item.icon} 
                          alt={item.label}
                          className="w-5 h-5 object-contain"
                        />
                      ) : (
                        <span>{item.icon}</span>
                      )}
                      <span>{item.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        )}
        
        {/* Contenido principal */}
        <div className="flex-1 bg-[#e9ecef] pb-4">
          <Outlet />
        </div>

        {/* Overlay para cerrar menú de perfil */}
        {showProfileMenu && (
          <div 
            className="fixed inset-0 z-30"
            onClick={() => setShowProfileMenu(false)}
          />
        )}

        {/* Modal para cambiar estado del restaurante */}
        {showStatusModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
              <div className="text-center">
                <div className="mb-4">
                  {selectedRestaurantStatus === 'open' ? (
                    <img src="/assets/images/puertaabierta.png" alt="Abierto" className="w-16 h-16 mx-auto mb-2" />
                  ) : (
                    <img src="/assets/images/puertacerrada.png" alt="Cerrado" className="w-16 h-16 mx-auto mb-2" />
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedRestaurantStatus === 'open' ? '¿Cerrar restaurante?' : '¿Abrir restaurante?'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedRestaurantStatus === 'open' 
                      ? 'Los clientes no podrán hacer pedidos hasta que lo abras nuevamente.'
                      : 'Los clientes podrán hacer pedidos una vez abierto.'
                    }
                  </p>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowStatusModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleToggleRestaurantStatus}
                    disabled={isChangingStatus}
                    className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors ${
                      isChangingStatus
                        ? 'bg-gray-400 cursor-not-allowed'
                        : selectedRestaurantStatus === 'open'
                        ? 'bg-red-500 hover:bg-red-600'
                        : 'bg-green-500 hover:bg-green-600'
                    }`}
                  >
                    {isChangingStatus ? '🔄 Procesando...' : (selectedRestaurantStatus === 'open' ? 'Cerrar' : 'Abrir')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <ErrorModal
          isOpen={errorModal.open}
          title={errorModal.title}
          message={errorModal.message}
          details={errorModal.details}
          onClose={() => setErrorModal({ open: false, message: '' })}
        />

        {/* Footer global */}
        <footer className="bg-[#e9ecef] py-5 w-full">
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-2 text-gray-600">
            <img
              src="/assets/ots/Servicio_a_Tiempo_logo_256x256.png"
              alt="Servicio a Tiempo"
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
            />
            <span className="text-xs sm:text-sm font-medium">© {new Date().getFullYear()} Servicio a Tiempo</span>
          </div>
        </footer>
      </div>
    </ProtectedRoute>
  );
} 